import * as React from 'react';
import { connect } from "react-redux";
import Alert from 'react-s-alert';
import { bindActionCreators } from "redux";
import { SubPageLayout } from "../../../components/layouts/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { FC, useEffect, useMemo } from 'react';
import { ChangeOptions, Form, ModelAction, actions } from 'react-redux-form';
import * as GroupItemActions from '../../../actions/groupItems';
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from '../../../dtos/Tmd.dtos';
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IGroupItemReduxFormState } from "../../../reducers/reactReduxForms/groupItem";

import './GroupItemCreateVersion.scss';

const reduxFormName: string = "reduxForms.groupItem";

interface GroupItemCreateVersionProps {
    currentGroupItem: Dtos.GroupItem;
    groupId?: number;
    form?: Dtos.GroupItem & IRegistrationFormState;
    formState?: Dtos.FormState;
    formProperties?: Dtos.FormProperty[];
    reduxFormState?: IGroupItemReduxFormState;
    groupItem?: Dtos.GroupItem;
    lookups?: Dtos.Lookup[];
    loadingGroupItem?: boolean;
    savingGroupItem?: boolean;
    creatingGroupItem?: boolean;
    createGroupItemSuccess?: boolean;
    validationFailures?: Dtos.ResponseError[];
    onClose?: Function;
    clearGroupItem?: (propertiesToClear?: string[]) => void;
    saveGroupItem?: (formData: Dtos.GroupItem, collectionFormData: Dtos.Collection) => void;
    createGroupItem?: (groupId: number) => void;
    loadForm?: (model: string, value: any) => ModelAction;
    changeForm?: (model: string, value: any, options?: ChangeOptions) => ModelAction;
}

const GroupItemCreateVersion: FC<GroupItemCreateVersionProps> = ({
    currentGroupItem,
    groupId,
    form,
    formState,
    formProperties,
    groupItem,
    lookups,
    loadingGroupItem,
    savingGroupItem,
    creatingGroupItem,
    createGroupItemSuccess,
    validationFailures,
    onClose,
    clearGroupItem,
    saveGroupItem,
    createGroupItem,
    loadForm,
    changeForm
}) => {

    // Create GroupItem 
    useEffect(() => {
        if (!groupItem && groupId) {
            createGroupItem(groupId);
        }
    }, [groupItem, groupId, createGroupItem]);

    // Load Form
    useEffect(() => {
        if (!creatingGroupItem && createGroupItemSuccess) {
            loadReduxForm();
        }
    }, [creatingGroupItem, createGroupItemSuccess, loadForm]);

    // Cleanup effect to clear the group item on component unmount
    useEffect(() => {
        return () => {
            clearGroupItem();
        };
    }, [clearGroupItem]);

    const handleOnSubmit = (data: Dtos.GroupItem, currentGroupItem: Dtos.GroupItem) => {
        if (!data.version) {
            handleOnSubmitFailed(data);
            return;
        }
        
        if (data) {
            const changes: Dtos.GroupItem = { 
                groupId: currentGroupItem.groupId, 
                groupItemTemplateId: currentGroupItem.groupItemTemplateId, 
                active: true 
            } as Dtos.GroupItem;
            saveGroupItem({ ...data, ...changes }, null);
        }
        onCancel();
    };

    const handleOnSubmitFailed = (_data: Dtos.GroupItem) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    const onCancel = () => {
        if (onClose) { onClose() }
    }

    const loadReduxForm = () => {

        let extraData: Dtos.GroupItem = { version: undefined } as Dtos.GroupItem;

        loadForm(reduxFormName, Object.assign({ ...groupItem }, extraData));
    }

    const renderContent = useMemo(() => {
        const formDisabled: boolean = savingGroupItem || (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            version: {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled,
                validators: {
                    required: value => value != undefined && value != null && value?.length > 0,
                },
            },
        };

        return (
            <Form 
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val, currentGroupItem)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {
                    renderFormControls(form, "groupItem", formComponents, lookups, validationFailures, formProperties, changeForm)
                }
                <div className="mt-4 mb-4 text-right">
                    <button 
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">Cancel</button>
                </div>
            </Form>
        );
    }, [
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        currentGroupItem
    ]);

    return (
        <SubPageLayout 
            loading={loadingGroupItem}
        >
            { renderContent }
        </SubPageLayout>
    ); 
}

const mapStateToProps = (state: ITmdState, ownProps: GroupItemCreateVersionProps): Partial<GroupItemCreateVersionProps> => {

    let groupItem: Dtos.GroupItem | undefined = !(state.groupItems.formData instanceof Array) ? state.groupItems.formData : undefined;
    
    return {
        currentGroupItem: ownProps.currentGroupItem,
        groupId: ownProps.groupId,
        groupItem: groupItem!,
        loadingGroupItem: state.groupItems.loadState && state.groupItems.loadState.status == RequestState.Pending,
        savingGroupItem: state.groupItems.saveState && state.groupItems.saveState.status == RequestState.Pending,
        creatingGroupItem: state.groupItems.createState && state.groupItems.createState.status == RequestState.Pending,
        createGroupItemSuccess: state.groupItems.createState && state.groupItems.createState.status == RequestState.Success,
        lookups: state.groupItems.lookups,
        form: state.reduxForms.groupItem,
        formState: state.groupItems.formState,
        formProperties: state.groupItems.formProperties,
        reduxFormState: state.reduxForms.formState.groupItem,
        validationFailures: state.groupItems.validationFailures,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupItemCreateVersionProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        clearGroupItem: bindActionCreators(GroupItemActions.Clear, dispatch),
        saveGroupItem: bindActionCreators(GroupItemActions.SaveGroupItem, dispatch),
        createGroupItem: bindActionCreators(GroupItemActions.CreateGroupItem, dispatch),

    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupItemCreateVersion);